/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useState } from "react";
import { Dialog, IconButton } from "@material-ui/core";
import { Skeleton, SwipeableDrawer, Tooltip } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { DesktopTimePicker } from "@mui/x-date-pickers/DesktopTimePicker";
import { ptBR } from "date-fns/locale";
import { RootStateOrAny, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { IoSearch, IoClose } from "react-icons/io5";
import { BsFilter } from "react-icons/bs";
import {
  FiMessageSquare,
  FiPaperclip,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiRotateCcw,
  FiEyeOff,
  FiCalendar,
  FiAlertTriangle,
} from "react-icons/fi";
import { RiCalendarEventLine, RiTimeLine } from "react-icons/ri";
import {
  useEnvioEmMassa,
  useSaldoEnvioEmMassa,
} from "services/api/AppWeb_WhatsApp/useWhatsAppConversas";
import {
  useWhatsAppTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
  useRestoreTemplate,
  TemplateAPI,
} from "services/api/AppWeb_WhatsApp/useWhatsAppTemplates";
import { useContatosWAV2 } from "services/api/AppWeb_WhatsApp/useWhatsAppContatos";
import { ContatoWA } from "services/api/AppWeb_WhatsApp/types";
import { useListarProdutos } from "services/api/Common_Produtos/listar-produtos";
import { useGetStandEspelho } from "services/products/getStandEspelho";
import { AuthState } from "store/ducks/auth/types";
import { useDebounce } from "hooks/useDebounce";
import { LeadsPropriosInterface } from "interfaces/wallet.interface";
import FiltersContent from "screens/leads/components/filtersContent";
import { INITIAL_FILTERS } from "screens/leads/components/tableGrid/constants";
import { buildFiltersBody } from "screens/leads/utils/buildFiltersBody";
import { ClearFilterGrid } from "assets/iconsSVG/clearFilterGrid";
import { avatarColor, iniciais } from "../utils";
import { SendHistoryPayload, TEMPLATE_TOKENS, contatoId } from "../types";
import * as S from "../styles";

interface EnvioMassaPanelProps {
  contatos?: ContatoWA[];
  loading?: boolean;
  onHistoryAppend: (payload: SendHistoryPayload[]) => void;
  selectionLocked?: boolean;
  onSendSuccess?: () => void;
}

const WHATSAPP_MASS_FILTERS_INITIAL: Record<string, any> = {
  ...INITIAL_FILTERS,
  TipoVisualizacao: 1,
};

const EMPTY_CONTATOS: ContatoWA[] = [];

interface EmpreendimentoOption {
  key: string;
  idDetalhe: string;
  idEnvio: string;
  nome: string;
}

interface EmpreendimentoMediaOption {
  id: string;
  url: string;
  nome: string;
  tipo: "IMAGEM" | "PLANTA" | "DOCUMENTO";
}

interface AttachmentPreview {
  id: string;
  nome: string;
  tipo: string;
  sizeLabel: string;
  isImage: boolean;
  previewUrl?: string;
  source: "UPLOAD" | "EMPREENDIMENTO";
  mediaId?: string;
}

function formatFileSize(bytes?: number): string {
  if (!bytes || Number.isNaN(bytes)) return "--";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageFile(file: File): boolean {
  if ((file.type || "").toLowerCase().startsWith("image/")) return true;
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(file.name || "");
}

function getFileUniqueKey(file: File): string {
  return `${file.name}::${file.size}::${file.lastModified}`;
}

const MAX_BULK_ATTACHMENTS = 5;

const EnvioMassaPanel: React.FC<EnvioMassaPanelProps> = ({
  contatos: contatosProp = [],
  loading: loadingProp = false,
  onHistoryAppend,
  selectionLocked = false,
  onSendSuccess,
}) => {
  const useWhatsappMassFilters = !selectionLocked;
  const [whatsappMassFilters, setWhatsappMassFilters] = useState(
    WHATSAPP_MASS_FILTERS_INITIAL
  );
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const debouncedBusca = useDebounce(busca, 500);
  const palavrasChaveBusca = debouncedBusca.trim();

  const filtersBody = useMemo(() => {
    const body = buildFiltersBody(
      whatsappMassFilters as LeadsPropriosInterface
    );
    delete body.PaginaAtual;
    delete body.TotalItensRetorno;
    delete body.TipoVisualizacao;
    delete body.PalavrasChave;
    return body;
  }, [whatsappMassFilters]);

  const { data: contatosV2Data, isFetching: contatosV2Loading } =
    useContatosWAV2(filtersBody, palavrasChaveBusca, useWhatsappMassFilters);

  const contatos = useMemo(() => {
    if (useWhatsappMassFilters) {
      return contatosV2Data?.contatos ?? EMPTY_CONTATOS;
    }
    return contatosProp;
  }, [useWhatsappMassFilters, contatosV2Data?.contatos, contatosProp]);

  const loading = useWhatsappMassFilters ? contatosV2Loading : loadingProp;
  const filtersCount = contatosV2Data?.quantidadeFiltrosAplicados ?? 0;

  const { codigoRegional365, codigoCargo2013 } = useSelector<
    RootStateOrAny,
    AuthState
  >((state) => state.user);
  const bulk = useEnvioEmMassa();
  const { data: saldoEnvio, isLoading: loadingSaldo } = useSaldoEnvioEmMassa();
  const { data: allTemplates = [], isLoading: templatesLoading } =
    useWhatsAppTemplates();
  const { data: padroesCompletos = [] } = useWhatsAppTemplates(true);
  const createTemplate = useCreateTemplate();
  const updateTemplate = useUpdateTemplate();
  const deleteTemplate = useDeleteTemplate();
  const restoreTemplateMut = useRestoreTemplate();

  // Templates visíveis (não ocultos)
  const templates = allTemplates.filter((t) => !t.oculto);
  // Templates padrão ocultos (restauráveis)
  const hiddenDefaults = padroesCompletos.filter((t) => t.oculto);
  const [showHidden, setShowHidden] = useState(false);

  const [selecionados, setSelecionados] = useState<Set<string>>(new Set());
  const [arquivosAnexo, setArquivosAnexo] = useState<File[]>([]);
  const [midiasEmpreendimentoAnexo, setMidiasEmpreendimentoAnexo] = useState<
    EmpreendimentoMediaOption[]
  >([]);
  const [anexoEmpreendimentoId, setAnexoEmpreendimentoId] = useState<
    string | undefined
  >(undefined);
  const [anexoEmpreendimentoNome, setAnexoEmpreendimentoNome] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [preparandoAnexoEmpreendimento, setPreparandoAnexoEmpreendimento] =
    useState(false);
  // Agendamento de disparo
  const [openAgendarModal, setOpenAgendarModal] = useState(false);
  const [openAvisoAgendamento, setOpenAvisoAgendamento] = useState(false);
  const [dataAgendada, setDataAgendada] = useState<Date | null>(null);
  const [horaAgendada, setHoraAgendada] = useState<Date | null>(null);

  const [activeTemplateId, setActiveTemplateId] = useState<string>("");
  const [mensagem, setMensagem] = useState("");

  const [openTemplateModal, setOpenTemplateModal] = useState(false);
  const [novoTemplateNome, setNovoTemplateNome] = useState("");
  const [novoTemplateConteudo, setNovoTemplateConteudo] = useState("");
  const [editingTemplate, setEditingTemplate] = useState<TemplateAPI | null>(
    null
  );
  const [openEmpreendimentoModal, setOpenEmpreendimentoModal] = useState(false);
  const [buscaEmpreendimento, setBuscaEmpreendimento] = useState("");
  const [empreendimentoSelecionadoKey, setEmpreendimentoSelecionadoKey] =
    useState<string>("");
  const [midiasSelecionadasIds, setMidiasSelecionadasIds] = useState<
    Set<string>
  >(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  );

  const { data: empreendimentosRaw = [], isLoading: loadingEmpreendimentos } =
    useListarProdutos({
      codigoRegional365: codigoRegional365 || "",
      enabled: !!codigoRegional365,
    });

  const empreendimentos = useMemo<EmpreendimentoOption[]>(
    () =>
      (empreendimentosRaw || [])
        .map((item: any, index: number) => {
          const idDetalhe = String(
            item?.idEmpreendimento || item?.id || item?.value || ""
          ).trim();
          const idEnvio = String(
            item?.idEmpreendimento365 ||
              item?.idEmpreendimento ||
              item?.id ||
              item?.value ||
              ""
          ).trim();
          const nome = String(
            item?.nomeEmpreendimento || item?.nome || item?.label || ""
          ).trim();
          const key = `${idDetalhe || "detalhe-vazio"}::${
            idEnvio || "envio-vazio"
          }::${nome || "sem-nome"}::${index}`;

          return {
            key,
            idDetalhe,
            idEnvio,
            nome,
          };
        })
        .filter(
          (item: EmpreendimentoOption) =>
            (!!item.idDetalhe || !!item.idEnvio) && !!item.nome
        ),
    [empreendimentosRaw]
  );

  const empreendimentosFiltrados = useMemo(
    () =>
      empreendimentos.filter((item) =>
        item.nome.toLowerCase().includes(buscaEmpreendimento.toLowerCase())
      ),
    [empreendimentos, buscaEmpreendimento]
  );

  const empreendimentoSelecionado = useMemo(
    () =>
      empreendimentos.find(
        (item) => item.key === empreendimentoSelecionadoKey
      ) || null,
    [empreendimentos, empreendimentoSelecionadoKey]
  );

  const {
    data: detalheEmpreendimento,
    isFetching: loadingMidiasEmpreendimento,
  } = useGetStandEspelho({
    idEmpreendimento: empreendimentoSelecionado?.idDetalhe || null,
    enabled: openEmpreendimentoModal && !!empreendimentoSelecionado?.idDetalhe,
  });

  const midiasEmpreendimento = useMemo<EmpreendimentoMediaOption[]>(() => {
    const container = Array.isArray(detalheEmpreendimento?.data)
      ? detalheEmpreendimento?.data?.[0]
      : detalheEmpreendimento?.data;

    if (!container) return [];

    const found = new Map<string, EmpreendimentoMediaOption>();
    const pushMedia = (
      url: string | undefined,
      nome: string | undefined,
      tipo: "IMAGEM" | "PLANTA" | "DOCUMENTO"
    ) => {
      const normalizedUrl = String(url || "").trim();
      if (!normalizedUrl) return;
      if (found.has(normalizedUrl)) return;
      found.set(normalizedUrl, {
        id: `${tipo}-${found.size + 1}`,
        url: normalizedUrl,
        nome: nome?.trim() || `${tipo} ${found.size + 1}`,
        tipo,
      });
    };

    (container?.fotosGaleria || []).forEach((item: any, idx: number) =>
      pushMedia(item?.url, item?.nome || `Imagem ${idx + 1}`, "IMAGEM")
    );
    (container?.fotosPlantas || []).forEach((item: any, idx: number) =>
      pushMedia(item?.url, item?.nome || `Planta ${idx + 1}`, "PLANTA")
    );

    const documentos = Array.isArray(container?.documentos)
      ? container.documentos
      : [];

    documentos.forEach((pasta: any) => {
      (pasta?.arquivos || []).forEach((arquivoItem: any, idx: number) => {
        pushMedia(
          arquivoItem?.urlDownload ||
            arquivoItem?.urlVisualizacao ||
            arquivoItem?.url,
          arquivoItem?.nome || `Documento ${idx + 1}`,
          "DOCUMENTO"
        );
      });
    });

    return Array.from(found.values());
  }, [detalheEmpreendimento]);

  const midiasSelecionadas = useMemo(
    () =>
      midiasEmpreendimento.filter((item) => midiasSelecionadasIds.has(item.id)),
    [midiasEmpreendimento, midiasSelecionadasIds]
  );
  const allMidiasSelecionadas = useMemo(() => {
    const vagasDisponiveis = Math.max(
      MAX_BULK_ATTACHMENTS - arquivosAnexo.length,
      0
    );
    const idsSelecionaveis = midiasEmpreendimento
      .map((item) => item.id)
      .slice(0, vagasDisponiveis);

    return (
      idsSelecionaveis.length > 0 &&
      idsSelecionaveis.every((id) => midiasSelecionadasIds.has(id))
    );
  }, [arquivosAnexo.length, midiasEmpreendimento, midiasSelecionadasIds]);

  const allContatoIds = useMemo(
    () => contatos.map((c) => contatoId(c)).filter(Boolean),
    [contatos]
  );

  useEffect(() => {
    if (!selectionLocked) return;
    // Em modo travado (carteira) seleciona todos, respeitando o saldo do dia.
    const limite = saldoEnvio ? saldoEnvio.saldo : allContatoIds.length;
    setSelecionados(new Set(allContatoIds.slice(0, Math.max(limite, 0))));
  }, [selectionLocked, allContatoIds, saldoEnvio]);

  useEffect(() => {
    if (selectionLocked) return;
    setSelecionados(new Set());
  }, [filtersBody, palavrasChaveBusca, selectionLocked]);

  function handleClearWhatsappMassFilters() {
    setBusca("");
    setWhatsappMassFilters((prev) => ({
      ...WHATSAPP_MASS_FILTERS_INITIAL,
      TipoVisualizacao: prev.TipoVisualizacao ?? 1,
    }));
  }

  function handleClearBuscaContatos() {
    setBusca("");
  }

  useEffect(() => {
    if (!openEmpreendimentoModal) return;
    if (empreendimentoSelecionadoKey) return;
    if (empreendimentos.length === 0) return;
    setEmpreendimentoSelecionadoKey(empreendimentos[0].key);
  }, [openEmpreendimentoModal, empreendimentoSelecionadoKey, empreendimentos]);

  useEffect(() => {
    setMidiasSelecionadasIds(new Set());
  }, [empreendimentoSelecionadoKey]);

  useEffect(() => {
    if (midiasEmpreendimentoAnexo.length > 0) return;
    setAnexoEmpreendimentoId(undefined);
    setAnexoEmpreendimentoNome("");
  }, [midiasEmpreendimentoAnexo.length]);

  const anexosPreview = useMemo<AttachmentPreview[]>(() => {
    const fromUpload = arquivosAnexo.map((file, index) => {
      const image = isImageFile(file);
      return {
        id: `upload-${file.name}-${file.size}-${file.lastModified}-${index}`,
        nome: file.name,
        tipo: file.type || "arquivo",
        sizeLabel: formatFileSize(file.size),
        isImage: image,
        previewUrl: image ? URL.createObjectURL(file) : undefined,
        source: "UPLOAD" as const,
      };
    });

    const fromEmpreendimento = midiasEmpreendimentoAnexo.map((media) => ({
      id: `media-${media.id}`,
      nome: media.nome,
      tipo: media.tipo,
      sizeLabel: "Empreendimento",
      isImage: media.tipo !== "DOCUMENTO",
      previewUrl: media.tipo !== "DOCUMENTO" ? media.url : undefined,
      source: "EMPREENDIMENTO" as const,
      mediaId: media.id,
    }));

    return [...fromUpload, ...fromEmpreendimento];
  }, [arquivosAnexo, midiasEmpreendimentoAnexo]);

  useEffect(
    () => () => {
      anexosPreview.forEach((item) => {
        if (item.source === "UPLOAD" && item.previewUrl) {
          URL.revokeObjectURL(item.previewUrl);
        }
      });
    },
    [anexosPreview]
  );

  const selecionadosLista = useMemo(
    () => contatos.filter((c) => selecionados.has(contatoId(c))),
    [contatos, selecionados]
  );

  const totalAnexos = arquivosAnexo.length + midiasEmpreendimentoAnexo.length;

  const limiteDiario = saldoEnvio?.limite ?? 0;
  const saldoDisponivel = saldoEnvio?.saldo ?? 0;
  const utilizadoHoje = saldoEnvio?.utilizado ?? 0;
  const semSaldo = !!saldoEnvio && saldoDisponivel <= 0;
  const excedeSaldo =
    !!saldoEnvio && selecionadosLista.length > saldoDisponivel;

  // Quantos contatos do filtro atual ainda cabem dentro do saldo do dia.
  const idsFiltrados = useMemo(
    () => contatos.map((c) => contatoId(c)).filter(Boolean),
    [contatos]
  );

  // Quando há saldo, "Selecionar todos" é limitado ao saldo disponível.
  const maxSelecionavelFiltrados = saldoEnvio
    ? Math.min(idsFiltrados.length, saldoDisponivel)
    : idsFiltrados.length;

  const allFilteredSelected = useMemo(() => {
    if (idsFiltrados.length === 0) return false;
    const selecionadosNoFiltro = idsFiltrados.filter((id) =>
      selecionados.has(id)
    ).length;
    return (
      maxSelecionavelFiltrados > 0 &&
      selecionadosNoFiltro >= maxSelecionavelFiltrados
    );
  }, [idsFiltrados, selecionados, maxSelecionavelFiltrados]);

  // Indica que o "Selecionar todos" foi limitado pelo saldo do dia.
  const selecaoLimitadaPeloSaldo =
    !!saldoEnvio && saldoDisponivel < idsFiltrados.length;

  function toggleContato(c: ContatoWA): void {
    const id = contatoId(c);
    if (!id) return;
    setSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        return next;
      }
      if (saldoEnvio && next.size >= saldoDisponivel) {
        toast.warn(
          `Você só pode selecionar ${saldoDisponivel} contato${
            saldoDisponivel !== 1 ? "s" : ""
          } hoje (saldo de envios).`
        );
        return next;
      }
      next.add(id);
      return next;
    });
  }

  function toggleTodosFiltrados(): void {
    setSelecionados((prev) => {
      const next = new Set(prev);

      if (allFilteredSelected) {
        idsFiltrados.forEach((id) => next.delete(id));
        return next;
      }

      if (!saldoEnvio) {
        idsFiltrados.forEach((id) => next.add(id));
        return next;
      }

      // Seleciona apenas até preencher o saldo disponível do dia.
      let vagas = saldoDisponivel - next.size;
      idsFiltrados.forEach((id) => {
        if (next.has(id)) return;
        if (vagas <= 0) return;
        next.add(id);
        vagas -= 1;
      });

      if (selecaoLimitadaPeloSaldo) {
        toast.info(
          `Seleção limitada a ${saldoDisponivel} contato${
            saldoDisponivel !== 1 ? "s" : ""
          } pelo saldo de envios de hoje.`
        );
      }
      return next;
    });
  }

  function inserirTokenNoTexto(token: string): void {
    setMensagem(
      (prev) => `${prev}${prev.endsWith(" ") || !prev ? "" : " "}${token}`
    );
  }

  function inserirTokenNoModal(token: string): void {
    setNovoTemplateConteudo(
      (prev) => `${prev}${prev.endsWith(" ") || !prev ? "" : " "}${token}`
    );
  }

  function handleMassPaste(e: React.ClipboardEvent<HTMLTextAreaElement>): void {
    const fileItem = Array.from(e.clipboardData.items).find(
      (item) => item.kind === "file"
    );
    const file = fileItem?.getAsFile();
    if (!file) return;
    e.preventDefault();
    setArquivosAnexo((prev) => {
      const anexosAtuais = prev.length + midiasEmpreendimentoAnexo.length;
      if (anexosAtuais >= MAX_BULK_ATTACHMENTS) {
        toast.warn(`Anexe no máximo ${MAX_BULK_ATTACHMENTS} arquivos.`);
        return prev;
      }
      const existing = new Set(prev.map((item) => getFileUniqueKey(item)));
      if (existing.has(getFileUniqueKey(file))) return prev;
      return [...prev, file];
    });
  }

  function selecionarTemplate(template: TemplateAPI): void {
    setActiveTemplateId(template.id);
    setMensagem(template.conteudo);
  }

  function abrirModalCriar(): void {
    setEditingTemplate(null);
    setNovoTemplateNome("");
    setNovoTemplateConteudo("");
    setOpenTemplateModal(true);
  }

  function abrirModalEditar(template: TemplateAPI): void {
    setEditingTemplate(template);
    setNovoTemplateNome(template.nome);
    setNovoTemplateConteudo(template.conteudo);
    setOpenTemplateModal(true);
  }

  function fecharModal(): void {
    setOpenTemplateModal(false);
    setEditingTemplate(null);
  }

  async function salvarTemplate(): Promise<void> {
    const nome = novoTemplateNome.trim();
    const conteudo = novoTemplateConteudo.trim();

    if (!nome) {
      toast.warn("Informe o nome do template");
      return;
    }
    if (!conteudo) {
      toast.warn("Informe o conteúdo do template");
      return;
    }

    if (editingTemplate) {
      await updateTemplate.mutateAsync({
        id: editingTemplate.id,
        nome,
        conteudo,
      });
      setActiveTemplateId(editingTemplate.id);
      setMensagem(conteudo);
    } else {
      await createTemplate.mutateAsync({ nome, conteudo });
    }

    fecharModal();
  }

  async function confirmarExclusao(): Promise<void> {
    if (!showDeleteConfirm) return;
    await deleteTemplate.mutateAsync(showDeleteConfirm);

    if (activeTemplateId === showDeleteConfirm) {
      setActiveTemplateId("");
      setMensagem("");
    }
    setShowDeleteConfirm(null);
  }

  async function ocultarTemplatePadrao(id: string): Promise<void> {
    await deleteTemplate.mutateAsync(id);
    if (activeTemplateId === id) {
      setActiveTemplateId("");
      setMensagem("");
    }
  }

  async function restaurarTemplatePadrao(id: string): Promise<void> {
    await restoreTemplateMut.mutateAsync(id);
  }

  async function enviarAgora(dataAgendamento?: Date): Promise<void> {
    if (
      !mensagem.trim() &&
      arquivosAnexo.length === 0 &&
      midiasEmpreendimentoAnexo.length === 0
    ) {
      toast.warn("Digite uma mensagem ou anexe um arquivo");
      return;
    }

    if (selecionadosLista.length === 0) {
      toast.warn("Selecione ao menos um contato");
      return;
    }

    if (totalAnexos > MAX_BULK_ATTACHMENTS) {
      toast.warn(`Anexe no máximo ${MAX_BULK_ATTACHMENTS} arquivos.`);
      return;
    }

    if (semSaldo) {
      toast.warn(
        `Limite diário de ${limiteDiario} envios atingido. Tente novamente amanhã.`
      );
      return;
    }

    if (excedeSaldo) {
      toast.warn(
        `Você selecionou ${selecionadosLista.length} contatos, mas só restam ${saldoDisponivel} envios hoje.`
      );
      return;
    }

    const dataEnvioISO = dataAgendamento
      ? dataAgendamento.toISOString()
      : undefined;

    setEnviando(true);
    const ids = selecionadosLista.map((c) => contatoId(c)).filter(Boolean);
    let envioConcluido = false;

    try {
      const linksMidiaEmpreendimento = midiasEmpreendimentoAnexo
        .map((item) => item.url)
        .filter(Boolean);

      await bulk.mutateAsync({
        idsLeadCorretor365: ids,
        mensagem: mensagem.trim(),
        idEmpreendimento365: anexoEmpreendimentoId,
        arquivos: arquivosAnexo.length > 0 ? arquivosAnexo : undefined,
        links: linksMidiaEmpreendimento.length
          ? linksMidiaEmpreendimento
          : undefined,
        dataEnvio: dataEnvioISO,
      });

      const historyPayloads: SendHistoryPayload[] = selecionadosLista.map(
        (contato) => ({
          leadId: contatoId(contato),
          leadNome: contato.nome || contato.telefone,
          mensagem:
            arquivosAnexo.length > 0 || midiasEmpreendimentoAnexo.length > 0
              ? `${mensagem.trim() || "Arquivo enviado"} (${
                  arquivosAnexo.length === 1 &&
                  midiasEmpreendimentoAnexo.length === 0
                    ? arquivosAnexo[0].name
                    : `${
                        arquivosAnexo.length + midiasEmpreendimentoAnexo.length
                      } arquivos`
                })`
              : mensagem.trim(),
          tipo: "CAMPANHA" as const,
          status: "ENVIADA",
        })
      );

      onHistoryAppend(historyPayloads);
      // Em modo travado a re-seleção (respeitando o saldo) é refeita pelo
      // useEffect quando o saldo atualizado chegar; aqui apenas limpamos.
      setSelecionados(new Set());
      setArquivosAnexo([]);
      setMidiasEmpreendimentoAnexo([]);
      setAnexoEmpreendimentoId(undefined);
      setAnexoEmpreendimentoNome("");
      setMensagem("");
      setActiveTemplateId("");
      setDataAgendada(null);
      setHoraAgendada(null);
      envioConcluido = true;
    } catch {
      // onError do hook já mostra o toast de erro
    }

    setEnviando(false);
    if (envioConcluido) {
      const totalEnviados = selecionadosLista.length;
      const sufixoContato = `${totalEnviados} contato${
        totalEnviados > 1 ? "s" : ""
      }`;
      if (dataEnvioISO) {
        toast.success(`Envio agendado com sucesso para ${sufixoContato}.`);
      } else {
        toast.success(`Mensagem enviada com sucesso para ${sufixoContato}.`);
      }
      onSendSuccess?.();
    }
  }

  const savingTemplate = createTemplate.isLoading || updateTemplate.isLoading;

  // Combina a data e o horário selecionados no modal em um único Date.
  const dataHoraAgendada = useMemo(() => {
    if (!dataAgendada || !horaAgendada) return null;
    const combinada = new Date(dataAgendada);
    combinada.setHours(
      horaAgendada.getHours(),
      horaAgendada.getMinutes(),
      0,
      0
    );
    return combinada;
  }, [dataAgendada, horaAgendada]);

  const agendamentoValido =
    !!dataHoraAgendada &&
    !Number.isNaN(dataHoraAgendada.getTime()) &&
    dataHoraAgendada.getTime() > Date.now();

  const mensagemVazia =
    !mensagem.trim() &&
    arquivosAnexo.length === 0 &&
    midiasEmpreendimentoAnexo.length === 0;

  const envioBloqueado =
    enviando ||
    semSaldo ||
    excedeSaldo ||
    selecionadosLista.length === 0 ||
    mensagemVazia;

  function abrirModalAgendamento(): void {
    if (mensagemVazia) {
      toast.warn("Digite uma mensagem ou anexe um arquivo");
      return;
    }
    if (selecionadosLista.length === 0) {
      toast.warn("Selecione ao menos um contato");
      return;
    }
    setOpenAgendarModal(true);
  }

  function confirmarAgendamento(): void {
    if (!agendamentoValido) {
      toast.warn("Escolha uma data e hora futuras para o agendamento");
      return;
    }
    setOpenAgendarModal(false);
    setOpenAvisoAgendamento(true);
  }

  async function confirmarAvisoAgendamento(): Promise<void> {
    setOpenAvisoAgendamento(false);
    if (!dataHoraAgendada) return;
    await enviarAgora(dataHoraAgendada);
  }

  const dialogPaperProps = {
    style: { borderRadius: 16, overflow: "hidden" },
  } as const;
  const tallDialogPaperProps = {
    style: {
      borderRadius: 16,
      overflow: "hidden",
      maxHeight: "90vh",
      display: "flex",
      flexDirection: "column",
    },
  } as const;

  function abrirSeletorEmpreendimento() {
    setOpenEmpreendimentoModal(true);
  }

  function limparAnexoSelecionado() {
    setArquivosAnexo([]);
    setMidiasEmpreendimentoAnexo([]);
    setMidiasSelecionadasIds(new Set());
    setAnexoEmpreendimentoId(undefined);
    setAnexoEmpreendimentoNome("");
  }

  function adicionarArquivos(files: File[]) {
    if (files.length === 0) return;
    setArquivosAnexo((prev) => {
      const vagasDisponiveis =
        MAX_BULK_ATTACHMENTS - prev.length - midiasEmpreendimentoAnexo.length;

      if (vagasDisponiveis <= 0) {
        toast.warn(`Anexe no máximo ${MAX_BULK_ATTACHMENTS} arquivos.`);
        return prev;
      }

      const next = [...prev];
      const existing = new Set(prev.map((item) => getFileUniqueKey(item)));
      files.slice(0, vagasDisponiveis).forEach((file) => {
        const key = getFileUniqueKey(file);
        if (!existing.has(key)) {
          existing.add(key);
          next.push(file);
        }
      });
      if (files.length > vagasDisponiveis) {
        toast.warn(`Anexe no máximo ${MAX_BULK_ATTACHMENTS} arquivos.`);
      }
      return next;
    });
  }

  function definirMidiasEmpreendimento(midias: EmpreendimentoMediaOption[]) {
    if (midias.length === 0) return;
    const vagasDisponiveis = Math.max(
      MAX_BULK_ATTACHMENTS - arquivosAnexo.length,
      0
    );
    const uniqueByUrl = new Map<string, EmpreendimentoMediaOption>();
    midias.forEach((midia) => {
      const key = String(midia.url || "").trim();
      if (!key) return;
      if (!uniqueByUrl.has(key)) uniqueByUrl.set(key, midia);
    });

    const nextMidias = Array.from(uniqueByUrl.values()).slice(
      0,
      vagasDisponiveis
    );
    setMidiasEmpreendimentoAnexo(nextMidias);

    if (uniqueByUrl.size > vagasDisponiveis) {
      toast.warn(`Anexe no máximo ${MAX_BULK_ATTACHMENTS} arquivos.`);
    }

    if (empreendimentoSelecionado?.idEnvio) {
      setAnexoEmpreendimentoId(empreendimentoSelecionado.idEnvio);
      setAnexoEmpreendimentoNome(empreendimentoSelecionado.nome || "");
    } else {
      setAnexoEmpreendimentoId(undefined);
      setAnexoEmpreendimentoNome("");
    }
  }

  function toggleSelecionarTodasMidias() {
    setMidiasSelecionadasIds((prev) => {
      const next = new Set(prev);
      const vagasDisponiveis = Math.max(
        MAX_BULK_ATTACHMENTS - arquivosAnexo.length,
        0
      );
      const idsAtuais = midiasEmpreendimento
        .map((item) => item.id)
        .slice(0, vagasDisponiveis);
      const allSelected =
        idsAtuais.length > 0 && idsAtuais.every((id) => next.has(id));

      if (allSelected) {
        idsAtuais.forEach((id) => next.delete(id));
      } else {
        idsAtuais.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  function removerAnexo(index: number): void {
    const target = anexosPreview[index];
    if (!target) return;

    if (target.source === "UPLOAD") {
      setArquivosAnexo((prev) =>
        prev.filter((_, idx) => {
          const currentId = `upload-${prev[idx].name}-${prev[idx].size}-${prev[idx].lastModified}-${idx}`;
          return currentId !== target.id;
        })
      );
      return;
    }

    if (target.source === "EMPREENDIMENTO" && target.mediaId) {
      setMidiasEmpreendimentoAnexo((prev) =>
        prev.filter((media) => media.id !== target.mediaId)
      );
    }
  }

  async function confirmarMidiaEmpreendimento() {
    if (midiasSelecionadas.length === 0) {
      toast.warn("Selecione ao menos uma mídia do empreendimento.");
      return;
    }

    if (
      arquivosAnexo.length + midiasSelecionadas.length >
      MAX_BULK_ATTACHMENTS
    ) {
      toast.warn(`Anexe no máximo ${MAX_BULK_ATTACHMENTS} arquivos.`);
      return;
    }

    try {
      setPreparandoAnexoEmpreendimento(true);
      definirMidiasEmpreendimento(midiasSelecionadas);
      setOpenEmpreendimentoModal(false);
      toast.success(
        `${midiasSelecionadas.length} mídia${
          midiasSelecionadas.length > 1 ? "s" : ""
        } anexada${midiasSelecionadas.length > 1 ? "s" : ""} do empreendimento.`
      );
    } finally {
      setPreparandoAnexoEmpreendimento(false);
    }
  }

  const panelContent = (
    <>
      <S.MassPage>
        <S.MassColumns singleColumn={selectionLocked}>
          {!selectionLocked && (
            <S.MassColumn>
              {/* <S.MassCard>
              <S.MassCardHeader>
                <S.MassCardTitle>
                  <FiUpload size={16} /> Importar Excel
                </S.MassCardTitle>
              </S.MassCardHeader>
              <S.MassImportBox>
                <p>Importe uma planilha (CSV, XLSX)</p>
                <label>
                  Selecionar arquivo
                  <input type="file" accept=".csv,.xlsx,.xls" />
                </label>
              </S.MassImportBox>
            </S.MassCard> */}

              <S.MassCard>
                <S.MassCardHeader>
                  <S.MassCardTitle>Contatos da Carteira</S.MassCardTitle>
                  <S.MassCardSub>
                    {selecionadosLista.length} selecionado(s)
                  </S.MassCardSub>
                </S.MassCardHeader>

                <S.MassSearchRow>
                  <S.MassSearchWrapper>
                    <IoSearch size={14} color="#94a3b8" />
                    <S.SearchInput
                      value={busca}
                      onChange={(e) => setBusca(e.target.value)}
                      placeholder="Buscar contato"
                    />
                    {busca && (
                      <S.ClearButton onClick={handleClearBuscaContatos}>
                        ×
                      </S.ClearButton>
                    )}
                  </S.MassSearchWrapper>

                  {useWhatsappMassFilters && (
                    <>
                      <Tooltip title="Abrir filtro" placement="top" arrow>
                        <S.MassFilterButton
                          type="button"
                          $active
                          onClick={() => setFiltersOpen(true)}
                          aria-label="Abrir filtros"
                        >
                          <BsFilter color="#FFFFFF" size={16} />
                        </S.MassFilterButton>
                      </Tooltip>

                      <Tooltip title="Limpar filtro" placement="top" arrow>
                        <S.MassFilterButton
                          type="button"
                          onClick={handleClearWhatsappMassFilters}
                          aria-label="Limpar filtros"
                        >
                          <ClearFilterGrid />
                          {filtersCount > 0 && (
                            <S.MassFiltersCount>
                              {filtersCount}
                            </S.MassFiltersCount>
                          )}
                        </S.MassFilterButton>
                      </Tooltip>
                    </>
                  )}
                </S.MassSearchRow>

                <S.MassToggleAll type="button" onClick={toggleTodosFiltrados}>
                  <input
                    type="checkbox"
                    checked={allFilteredSelected}
                    readOnly
                  />
                  <span>Selecionar todos</span>
                  {selecaoLimitadaPeloSaldo && (
                    <S.MassToggleAllHint>
                      até {saldoDisponivel} (saldo de hoje)
                    </S.MassToggleAllHint>
                  )}
                </S.MassToggleAll>

                <S.MassContactsList>
                  {loading &&
                    [1, 2, 3, 4].map((item) => (
                      <S.MassContactItem key={item}>
                        <Skeleton variant="circular" width={36} height={36} />
                        <S.MassSkeletonTextBlock>
                          <Skeleton variant="text" width="70%" height={18} />
                          <Skeleton variant="text" width="45%" height={14} />
                        </S.MassSkeletonTextBlock>
                      </S.MassContactItem>
                    ))}

                  {!loading &&
                    contatos.map((contato) => {
                      const id = contatoId(contato);
                      const checked = selecionados.has(id);

                      return (
                        <S.MassContactItem
                          key={id}
                          onClick={() => toggleContato(contato)}
                        >
                          <input type="checkbox" checked={checked} readOnly />
                          <S.MassContactLeft>
                            <S.Avatar bgColor={avatarColor(id)} size={34}>
                              {iniciais(contato.nome || contato.telefone)}
                              {contato.fotoUrl && (
                                <S.AvatarImg
                                  src={contato.fotoUrl}
                                  alt=""
                                  referrerPolicy="no-referrer"
                                  onError={(e) => {
                                    (
                                      e.target as HTMLImageElement
                                    ).style.display = "none";
                                  }}
                                />
                              )}
                            </S.Avatar>
                            <S.MassContactMeta>
                              <strong>
                                {contato.nome || contato.telefone}
                              </strong>
                              <span>{contato.telefone}</span>
                            </S.MassContactMeta>
                          </S.MassContactLeft>
                        </S.MassContactItem>
                      );
                    })}

                  {!loading && contatos.length === 0 && (
                    <S.HistoryEmpty>Nenhum contato encontrado.</S.HistoryEmpty>
                  )}
                </S.MassContactsList>
              </S.MassCard>
            </S.MassColumn>
          )}

          <S.MassColumn>
            <S.MassCard>
              <S.MassCardHeader>
                <S.MassCardTitle>
                  <FiMessageSquare size={16} /> Escrever Mensagem
                </S.MassCardTitle>
                <S.MassNewTemplateAction
                  type="button"
                  onClick={abrirModalCriar}
                >
                  <FiPlus size={14} /> Novo Template
                </S.MassNewTemplateAction>
              </S.MassCardHeader>

              {selectionLocked && (
                <S.MassCardSubSpaced>
                  {selecionadosLista.length} contato
                  {selecionadosLista.length !== 1 ? "s" : ""} da carteira
                  selecionado{selecionadosLista.length !== 1 ? "s" : ""}
                </S.MassCardSubSpaced>
              )}

              {/* Na tela standalone o indicador já aparece no toolbar; aqui
                  (modal da carteira) mostramos o pill dentro do card. */}
              {selectionLocked && !loadingSaldo && saldoEnvio && (
                <S.MassSaldoRow>
                  <S.EnviosHojePill>
                    <S.EnviosHojeDot />
                    <S.EnviosHojeValue>{utilizadoHoje}</S.EnviosHojeValue>
                    <S.EnviosHojeMuted>
                      / {limiteDiario} envios hoje
                    </S.EnviosHojeMuted>
                  </S.EnviosHojePill>
                </S.MassSaldoRow>
              )}

              <S.MassTemplateLabel>TEMPLATES DE MENSAGEM</S.MassTemplateLabel>
              <S.MassTemplateBar>
                {templatesLoading && (
                  <>
                    <Skeleton
                      variant="rounded"
                      width={100}
                      height={28}
                      style={{ borderRadius: 14 }}
                    />
                    <Skeleton
                      variant="rounded"
                      width={120}
                      height={28}
                      style={{ borderRadius: 14 }}
                    />
                    <Skeleton
                      variant="rounded"
                      width={90}
                      height={28}
                      style={{ borderRadius: 14 }}
                    />
                  </>
                )}
                {!templatesLoading &&
                  templates.map((template) => (
                    <S.MassTemplateItem key={template.id}>
                      <S.MassTemplateChip
                        active={template.id === activeTemplateId}
                        onClick={() => selecionarTemplate(template)}
                      >
                        {template.nome}
                        {template.templatePadrao && (
                          <S.MassTemplateBadge>padrão</S.MassTemplateBadge>
                        )}
                      </S.MassTemplateChip>
                      <S.MassTemplateHoverActions>
                        {template.templatePadrao ? (
                          <S.MassTemplateIconBtn
                            type="button"
                            variant="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              ocultarTemplatePadrao(template.id);
                            }}
                            aria-label={`Ocultar ${template.nome}`}
                          >
                            <FiEyeOff size={11} />
                          </S.MassTemplateIconBtn>
                        ) : (
                          <>
                            <S.MassTemplateIconBtn
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                abrirModalEditar(template);
                              }}
                              aria-label={`Editar ${template.nome}`}
                            >
                              <FiEdit2 size={11} />
                            </S.MassTemplateIconBtn>
                            <S.MassTemplateIconBtn
                              type="button"
                              variant="danger"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowDeleteConfirm(template.id);
                              }}
                              aria-label={`Excluir ${template.nome}`}
                            >
                              <FiTrash2 size={11} />
                            </S.MassTemplateIconBtn>
                          </>
                        )}
                      </S.MassTemplateHoverActions>
                    </S.MassTemplateItem>
                  ))}
                {!templatesLoading && templates.length === 0 && (
                  <S.MassCardSub>
                    Nenhum template. Crie um novo acima.
                  </S.MassCardSub>
                )}
              </S.MassTemplateBar>

              {hiddenDefaults.length > 0 && (
                <S.MassHiddenSection>
                  <S.MassHiddenToggle
                    type="button"
                    onClick={() => setShowHidden((v) => !v)}
                  >
                    <FiRotateCcw size={13} />
                    {hiddenDefaults.length} template
                    {hiddenDefaults.length > 1 ? "s" : ""} padrão oculto
                    {hiddenDefaults.length > 1 ? "s" : ""}
                    <S.MassHiddenArrow open={showHidden}>›</S.MassHiddenArrow>
                  </S.MassHiddenToggle>
                  {showHidden && (
                    <S.MassHiddenList>
                      {hiddenDefaults.map((template) => (
                        <S.MassHiddenItem key={template.id}>
                          <S.MassHiddenName>{template.nome}</S.MassHiddenName>
                          <S.MassHiddenRestore
                            type="button"
                            onClick={() => restaurarTemplatePadrao(template.id)}
                          >
                            <FiRotateCcw size={12} />
                            Restaurar
                          </S.MassHiddenRestore>
                        </S.MassHiddenItem>
                      ))}
                    </S.MassHiddenList>
                  )}
                </S.MassHiddenSection>
              )}

              <S.MassTokens>
                {TEMPLATE_TOKENS.map((token) => (
                  <S.MassToken
                    key={token}
                    onClick={() => inserirTokenNoTexto(token)}
                  >
                    {token}
                  </S.MassToken>
                ))}
              </S.MassTokens>

              <S.MassTextarea
                value={mensagem}
                onChange={(e) => setMensagem(e.target.value)}
                onPaste={handleMassPaste}
                placeholder="Digite a mensagem para envio..."
              />

              <S.MassAttachmentRow>
                <S.MassAttachmentActions>
                  <S.MassAttachmentButton>
                    <FiPaperclip size={15} />
                    Anexar arquivo
                    <input
                      type="file"
                      multiple
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        adicionarArquivos(files);
                        e.target.value = "";
                      }}
                    />
                  </S.MassAttachmentButton>
                  <S.MassAttachmentLibraryButton
                    type="button"
                    onClick={abrirSeletorEmpreendimento}
                  >
                    Selecionar por empreendimento
                  </S.MassAttachmentLibraryButton>
                </S.MassAttachmentActions>
                <S.MassAttachmentName>
                  {arquivosAnexo.length > 0 ||
                  midiasEmpreendimentoAnexo.length > 0
                    ? `${
                        arquivosAnexo.length +
                          midiasEmpreendimentoAnexo.length ===
                        1
                          ? anexosPreview[0]?.nome || "1 arquivo selecionado"
                          : `${
                              arquivosAnexo.length +
                              midiasEmpreendimentoAnexo.length
                            } arquivos selecionados`
                      }${
                        anexoEmpreendimentoNome
                          ? ` · ${anexoEmpreendimentoNome}`
                          : ""
                      }`
                    : "Nenhum arquivo selecionado"}
                </S.MassAttachmentName>
              </S.MassAttachmentRow>

              {anexosPreview.length > 0 && (
                <S.MassAttachmentPreviewScroll>
                  <S.MassAttachmentPreviewGrid>
                    {anexosPreview.map((item, index) => (
                      <S.MassAttachmentPreviewCard key={item.id}>
                        <S.MassAttachmentPreviewThumb>
                          {item.isImage && item.previewUrl ? (
                            <img src={item.previewUrl} alt={item.nome} />
                          ) : (
                            <span>ARQUIVO</span>
                          )}
                        </S.MassAttachmentPreviewThumb>
                        <S.MassAttachmentPreviewMeta>
                          <strong>{item.nome}</strong>
                          <span>{item.sizeLabel}</span>
                        </S.MassAttachmentPreviewMeta>
                        <S.MassAttachmentPreviewRemove
                          type="button"
                          onClick={() => removerAnexo(index)}
                          aria-label={`Remover ${item.nome}`}
                        >
                          <IoClose size={14} />
                        </S.MassAttachmentPreviewRemove>
                      </S.MassAttachmentPreviewCard>
                    ))}
                  </S.MassAttachmentPreviewGrid>
                </S.MassAttachmentPreviewScroll>
              )}

              <S.MassSelectionFooter>
                <S.MassSecondaryButton
                  type="button"
                  onClick={() => {
                    setMensagem("");
                    limparAnexoSelecionado();
                    setDataAgendada(null);
                    setHoraAgendada(null);
                  }}
                >
                  Limpar
                </S.MassSecondaryButton>
                <S.MassFooterActions>
                  <S.MassAgendarButton
                    type="button"
                    onClick={abrirModalAgendamento}
                    disabled={envioBloqueado}
                  >
                    Agendar
                  </S.MassAgendarButton>
                  <S.MassSendButton
                    type="button"
                    onClick={() => enviarAgora()}
                    disabled={envioBloqueado}
                  >
                    {enviando ? "Enviando..." : "Enviar Agora"}
                  </S.MassSendButton>
                </S.MassFooterActions>
              </S.MassSelectionFooter>
            </S.MassCard>
          </S.MassColumn>
        </S.MassColumns>
      </S.MassPage>

      {/* Modal Agendar Disparo */}
      <Dialog
        open={openAgendarModal}
        onClose={() => setOpenAgendarModal(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <S.AgendarModalHeader>
          <S.AgendarModalTitle>
            <S.AgendarModalTitleIcon>
              <FiCalendar size={16} />
            </S.AgendarModalTitleIcon>
            Agendar Disparo
          </S.AgendarModalTitle>
          <IconButton
            size="small"
            onClick={() => setOpenAgendarModal(false)}
            aria-label="Fechar"
          >
            <IoClose size={20} />
          </IconButton>
        </S.AgendarModalHeader>

        <S.AgendarModalBody>
          <LocalizationProvider
            adapterLocale={ptBR}
            dateAdapter={AdapterDateFns}
          >
            <S.AgendarModalLabel>DATA</S.AgendarModalLabel>
            <S.AgendarPickerWrapper>
              <DesktopDatePicker
                value={dataAgendada}
                onChange={(val) => setDataAgendada(val as Date | null)}
                minDate={new Date()}
                inputFormat="dd/MM/yyyy"
                components={{
                  OpenPickerIcon: () => (
                    <RiCalendarEventLine size={18} color="#64748b" />
                  ),
                }}
                renderInput={(params) => (
                  <S.AgendarPickerInput {...params} size="small" fullWidth />
                )}
              />
            </S.AgendarPickerWrapper>

            <S.AgendarModalLabelSpaced>HORÁRIO</S.AgendarModalLabelSpaced>
            <S.AgendarPickerWrapper>
              <DesktopTimePicker
                value={horaAgendada}
                onChange={(val) => setHoraAgendada(val as Date | null)}
                ampm={false}
                components={{
                  OpenPickerIcon: () => (
                    <RiTimeLine size={18} color="#64748b" />
                  ),
                }}
                renderInput={(params) => (
                  <S.AgendarPickerInput {...params} size="small" fullWidth />
                )}
              />
            </S.AgendarPickerWrapper>
          </LocalizationProvider>
        </S.AgendarModalBody>

        <S.AgendarModalFooter>
          <S.MassSecondaryButton
            type="button"
            onClick={() => setOpenAgendarModal(false)}
          >
            Cancelar
          </S.MassSecondaryButton>
          <S.AgendarConfirmButton
            type="button"
            onClick={confirmarAgendamento}
            disabled={!agendamentoValido}
          >
            Confirmar
          </S.AgendarConfirmButton>
        </S.AgendarModalFooter>
      </Dialog>

      {/* Modal Aviso Importante (agendamento) */}
      <Dialog
        open={openAvisoAgendamento}
        onClose={() => setOpenAvisoAgendamento(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <S.AvisoAgendamentoBody>
          <S.AvisoAgendamentoIcon>
            <FiAlertTriangle size={26} />
          </S.AvisoAgendamentoIcon>
          <S.AvisoAgendamentoTitle>Aviso Importante</S.AvisoAgendamentoTitle>
          <S.AvisoAgendamentoText>
            Para que a mensagem seja enviada na data e hora da programação, você
            deve estar{" "}
            <strong>
              autenticado e com a ferramenta de envios aberta e conectada
            </strong>{" "}
            ao sistema.
          </S.AvisoAgendamentoText>
          <S.AvisoAgendamentoButton
            type="button"
            onClick={confirmarAvisoAgendamento}
            disabled={enviando}
          >
            {enviando ? "Agendando..." : "Entendido, Agendar!"}
          </S.AvisoAgendamentoButton>
        </S.AvisoAgendamentoBody>
      </Dialog>

      {/* Modal Criar/Editar Template */}
      <Dialog
        open={openTemplateModal}
        onClose={fecharModal}
        maxWidth="sm"
        fullWidth
        PaperProps={dialogPaperProps}
      >
        <S.MassTemplateModalHeader>
          <S.MassTemplateModalTitle>
            {editingTemplate ? "Editar Template" : "Novo Template"}
          </S.MassTemplateModalTitle>
          <IconButton size="small" onClick={fecharModal} aria-label="Fechar">
            <IoClose size={20} />
          </IconButton>
        </S.MassTemplateModalHeader>

        <S.MassTemplateModalBody>
          <S.MassTemplateModalLabel>NOME DO TEMPLATE</S.MassTemplateModalLabel>
          <S.MassTemplateModalInput
            value={novoTemplateNome}
            onChange={(e) => setNovoTemplateNome(e.target.value)}
            placeholder="Ex: Boas-vindas"
          />

          <S.MassTemplateModalLabelSpaced>
            CONTEÚDO DA MENSAGEM
          </S.MassTemplateModalLabelSpaced>
          <S.MassTokens>
            {TEMPLATE_TOKENS.map((token) => (
              <S.MassToken
                key={token}
                onClick={() => inserirTokenNoModal(token)}
              >
                {token}
              </S.MassToken>
            ))}
          </S.MassTokens>
          <S.MassTemplateModalTextarea
            value={novoTemplateConteudo}
            onChange={(e) => setNovoTemplateConteudo(e.target.value)}
            placeholder="Digite o conteúdo do template..."
          />
        </S.MassTemplateModalBody>

        <S.MassTemplateModalFooter>
          <S.MassSecondaryButton type="button" onClick={fecharModal}>
            Cancelar
          </S.MassSecondaryButton>
          <S.MassSendButton
            type="button"
            onClick={salvarTemplate}
            disabled={savingTemplate}
          >
            {(() => {
              if (savingTemplate) return "Salvando...";
              if (editingTemplate) return "Salvar Alterações";
              return "Salvar Template";
            })()}
          </S.MassSendButton>
        </S.MassTemplateModalFooter>
      </Dialog>

      {/* Modal Confirmar Exclusão */}
      {showDeleteConfirm && (
        <S.ConfirmOverlay onClick={() => setShowDeleteConfirm(null)}>
          <S.ConfirmCard onClick={(e) => e.stopPropagation()}>
            <S.ConfirmBody>
              <S.ConfirmTitle>Excluir template?</S.ConfirmTitle>
              <S.ConfirmText>
                Essa ação não pode ser desfeita. O template será removido
                permanentemente.
              </S.ConfirmText>
            </S.ConfirmBody>
            <S.ConfirmActions>
              <S.ConfirmBtnCancel
                type="button"
                onClick={() => setShowDeleteConfirm(null)}
              >
                Cancelar
              </S.ConfirmBtnCancel>
              <S.ConfirmBtnDanger
                type="button"
                onClick={confirmarExclusao}
                disabled={deleteTemplate.isLoading}
              >
                {deleteTemplate.isLoading ? "Excluindo..." : "Excluir"}
              </S.ConfirmBtnDanger>
            </S.ConfirmActions>
          </S.ConfirmCard>
        </S.ConfirmOverlay>
      )}

      <Dialog
        open={openEmpreendimentoModal}
        onClose={() => setOpenEmpreendimentoModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={tallDialogPaperProps}
      >
        <S.MassAssetModalHeader>
          <S.MassAssetModalTitle>Selecionar Arquivos</S.MassAssetModalTitle>
          <S.MassAssetModalSub>
            Escolha os documentos por empreendimento
          </S.MassAssetModalSub>
          <IconButton
            size="small"
            onClick={() => setOpenEmpreendimentoModal(false)}
            aria-label="Fechar"
          >
            <IoClose size={20} />
          </IconButton>
        </S.MassAssetModalHeader>

        <S.MassAssetModalBody>
          <S.MassAssetSidebar>
            <S.MassSearchWrapper>
              <IoSearch size={14} color="#94a3b8" />
              <S.SearchInput
                value={buscaEmpreendimento}
                onChange={(e) => setBuscaEmpreendimento(e.target.value)}
                placeholder="Buscar empreendimento..."
              />
              {buscaEmpreendimento && (
                <S.ClearButton onClick={() => setBuscaEmpreendimento("")}>
                  ×
                </S.ClearButton>
              )}
            </S.MassSearchWrapper>

            <S.MassAssetEmpList>
              {loadingEmpreendimentos &&
                [1, 2, 3, 4].map((idx) => (
                  <S.MassAssetEmpItem key={idx} type="button">
                    <Skeleton variant="text" width="90%" height={26} />
                  </S.MassAssetEmpItem>
                ))}

              {!loadingEmpreendimentos &&
                empreendimentosFiltrados.map((emp) => (
                  <S.MassAssetEmpItem
                    key={emp.key}
                    type="button"
                    active={emp.key === empreendimentoSelecionadoKey}
                    onClick={() => setEmpreendimentoSelecionadoKey(emp.key)}
                  >
                    <span>{emp.nome}</span>
                    <span className="arrow">›</span>
                  </S.MassAssetEmpItem>
                ))}

              {!loadingEmpreendimentos &&
                empreendimentosFiltrados.length === 0 && (
                  <S.MassAssetEmpty>
                    Nenhum empreendimento encontrado.
                  </S.MassAssetEmpty>
                )}
            </S.MassAssetEmpList>
          </S.MassAssetSidebar>

          <S.MassAssetContent>
            <S.MassAssetContentTitle>
              {empreendimentoSelecionado?.nome || "Selecione um empreendimento"}
            </S.MassAssetContentTitle>
            <S.MassAssetContentSub>ARQUIVOS DISPONÍVEIS</S.MassAssetContentSub>
            {midiasEmpreendimento.length > 0 && (
              <S.MassAssetSelectAll
                type="button"
                onClick={toggleSelecionarTodasMidias}
              >
                <input
                  type="checkbox"
                  checked={allMidiasSelecionadas}
                  readOnly
                />
                {allMidiasSelecionadas ? "Desmarcar todos" : "Selecionar até 5"}
              </S.MassAssetSelectAll>
            )}

            <S.MassAssetFilesList>
              {loadingMidiasEmpreendimento &&
                [1, 2, 3].map((idx) => (
                  <S.MassAssetFileRow key={idx}>
                    <Skeleton variant="rounded" width={36} height={36} />
                    <S.MassSkeletonTextBlock>
                      <Skeleton variant="text" width="60%" height={20} />
                      <Skeleton variant="text" width="30%" height={14} />
                    </S.MassSkeletonTextBlock>
                    <Skeleton variant="circular" width={22} height={22} />
                  </S.MassAssetFileRow>
                ))}

              {!loadingMidiasEmpreendimento &&
                midiasEmpreendimento.map((midia) => (
                  <S.MassAssetFileRow
                    key={midia.id}
                    type="button"
                    active={midiasSelecionadasIds.has(midia.id)}
                    onClick={() =>
                      setMidiasSelecionadasIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(midia.id)) {
                          next.delete(midia.id);
                        } else if (
                          arquivosAnexo.length + next.size >=
                          MAX_BULK_ATTACHMENTS
                        ) {
                          toast.warn(
                            `Anexe no máximo ${MAX_BULK_ATTACHMENTS} arquivos.`
                          );
                        } else {
                          next.add(midia.id);
                        }
                        return next;
                      })
                    }
                  >
                    <S.MassAssetFileIcon>
                      {midia.tipo === "DOCUMENTO" ? (
                        "D"
                      ) : (
                        <img
                          src={midia.url}
                          alt={midia.nome}
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                      )}
                    </S.MassAssetFileIcon>
                    <S.MassAssetFileMeta>
                      <strong>{midia.nome}</strong>
                      <span>{midia.tipo}</span>
                    </S.MassAssetFileMeta>
                    <S.MassAssetFileCheck
                      checked={midiasSelecionadasIds.has(midia.id)}
                    />
                  </S.MassAssetFileRow>
                ))}

              {!loadingMidiasEmpreendimento &&
                midiasEmpreendimento.length === 0 && (
                  <S.MassAssetEmpty>
                    Não há mídias para este empreendimento.
                  </S.MassAssetEmpty>
                )}
            </S.MassAssetFilesList>
          </S.MassAssetContent>
        </S.MassAssetModalBody>

        <S.MassAssetModalFooter>
          <S.MassSecondaryButton
            type="button"
            onClick={() => {
              limparAnexoSelecionado();
            }}
          >
            Limpar
          </S.MassSecondaryButton>
          <S.MassSendButton
            type="button"
            onClick={confirmarMidiaEmpreendimento}
            disabled={
              preparandoAnexoEmpreendimento || midiasSelecionadas.length === 0
            }
          >
            {preparandoAnexoEmpreendimento ? "Preparando..." : "Confirmar"}
          </S.MassSendButton>
        </S.MassAssetModalFooter>
      </Dialog>
    </>
  );

  if (!useWhatsappMassFilters) {
    return panelContent;
  }

  return (
    <>
      {panelContent}
      <SwipeableDrawer
        open={filtersOpen}
        anchor="right"
        onClose={() => setFiltersOpen(false)}
        onOpen={() => setFiltersOpen(true)}
        PaperProps={{
          sx: {
            width: 392,
            borderRadius: "8px 0 0 8px",
          },
        }}
      >
        <FiltersContent
          codigoCargo2013={codigoCargo2013}
          isOpen={filtersOpen}
          filters={whatsappMassFilters}
          setFilters={setWhatsappMassFilters}
          onApply={() => setFiltersOpen(false)}
        />
      </SwipeableDrawer>
    </>
  );
};

export default EnvioMassaPanel;
